import classnames from 'classnames';
import React, { useCallback } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import Arrow from '../Arrow/Arrow';
import { SortFieldDisplayProps, SortState } from '../types';

import * as styles from './MultiSort.module.scss';

export interface MultiSortProps {
  sortFields: SortFieldDisplayProps[];

  onSorted(sort: SortFieldDisplayProps): void;

  onClickAway(): void;

  isVisible: boolean;
  offset: number;
  selectedField?: SortState;
}

interface SortFieldProps {
  field: SortFieldDisplayProps;
  isSelected: boolean;
  onSorted: (field: SortFieldDisplayProps) => void;
}

const SortField: React.FC<SortFieldProps> = ({ field, isSelected, onSorted }) => {
  const { sortDirection, label } = field;
  return (
    <div
      className={classnames(styles['MultiSort__sortField'], { [styles['MultiSort__sortField--selected']]: isSelected })}
      data-test-id="multiSortField"
      onClick={(e) => {
        e.stopPropagation();
        onSorted(field);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          onSorted(field);
        }
      }}
      tabIndex={0}
    >
      {
        <div data-test-id="sortArrows">
          <Arrow
            colour={isSelected ? 'text-heading' : 'text-secondary-interactive'}
            isVisible={true}
            sortDirection={sortDirection}
          />
        </div>
      }
      <span>{label}</span>
    </div>
  );
};

export const MultiSort: React.FC<MultiSortProps> = ({
  offset,
  isVisible,
  selectedField,
  sortFields,
  onSorted,
  onClickAway,
}) => {
  const style = { top: offset };

  if (!isVisible) {
    return null;
  }

  const handleOutsideClick = useCallback(() => {
    onClickAway && onClickAway();
  }, [onClickAway]);

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <div className={styles['MultiSort']} style={style} data-test-id="multiSort">
        <div className={styles['MultiSort__header']}>Sort by</div>
        {sortFields.map((field: SortFieldDisplayProps) => (
          <SortField
            key={field.sortKey + field.sortDirection}
            onSorted={onSorted}
            field={field}
            isSelected={field.sortKey === selectedField?.sortKey && field.sortDirection === selectedField.sortDirection}
          />
        ))}
      </div>
    </OutsideClickHandler>
  );
};
